import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, ArrowsCounterClockwise, Info, ShieldCheck, ShieldSlash, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/currency";

const CURRENCIES = ["USD", "KWD", "SAR", "AED", "EUR", "GBP", "EGP"];

/**
 * PaymentMethodSection — Payment method + Save Card status + tenant currency picker.
 * Placement anchor for deep-link `/org#payment-method` used by the dunning email.
 */
export default function PaymentMethodSection({ tenant, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [currency, setCurrency] = useState(tenant?.preferred_currency || "USD");

  useEffect(() => { if (tenant?.preferred_currency) setCurrency(tenant.preferred_currency); }, [tenant?.preferred_currency]);

  const sub = tenant?.subscription;
  if (!sub) return null;

  const hasCard = Boolean(sub.tap_card_last4);
  const brand = (sub.tap_card_brand || "CARD").toUpperCase();
  const last4 = sub.tap_card_last4 || "••••";
  const exp = sub.tap_card_exp || null;
  const recurringOff = Boolean(sub.needs_manual_renewal);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  const startUpdate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tap/charges/checkout", { intent: "card_update" });
      if (data.transaction_url) {
        toast.info("Redirecting to Tap to verify your new card…");
        window.location.href = data.transaction_url;
      } else {
        toast.error("Tap did not return a redirect URL. Please retry.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start card update.");
    } finally { setBusy(false); }
  };

  const runProbe = async () => {
    setProbing(true);
    try {
      const { data } = await api.post("/tap/save-card-probe");
      if (data.save_card_enabled) {
        toast.success(data.message || "Save Card verified — recurring billing armed.");
      } else {
        toast.error(data.message || "Save Card still disabled on Tap.");
      }
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Probe failed.");
    } finally { setProbing(false); }
  };

  const saveCurrency = async (val) => {
    if (val === (tenant?.preferred_currency || "USD")) return;
    setSavingCurrency(true);
    setCurrency(val);
    try {
      await api.patch("/tenant/currency", { currency: val });
      toast.success(`Tenant currency set to ${val}. Future charges will be billed in ${val}.`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update currency.");
      setCurrency(tenant?.preferred_currency || "USD");
    } finally { setSavingCurrency(false); }
  };

  return (
    <section
      id="payment-method"
      className="border border-zinc-950 mt-8 scroll-mt-24"
      data-testid="payment-method-section"
    >
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span>payment.method</span>
        {recurringOff ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.14em] uppercase bg-amber-500 text-zinc-950 px-2 py-0.5"
            data-testid="save-card-status-off"
          >
            <ShieldSlash size={11} weight="bold" /> RECURRING OFF
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.14em] uppercase bg-emerald-500 text-zinc-950 px-2 py-0.5"
            data-testid="save-card-status-on"
          >
            <ShieldCheck size={11} weight="bold" /> RECURRING ARMED
          </span>
        )}
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2 flex items-center gap-4">
          <div className="w-14 h-10 border border-zinc-950 flex items-center justify-center bg-zinc-50 shrink-0">
            <CreditCard size={22} weight="bold" className="text-zinc-950" />
          </div>
          <div className="flex-1">
            {hasCard ? (
              <>
                <div className="font-display font-bold text-lg tracking-tight" data-testid="payment-method-card">
                  {brand} •••• {last4}
                </div>
                <div className="text-xs text-zinc-500 font-mono tnum mt-0.5">
                  {exp && <span data-testid="payment-method-exp">Exp {exp}</span>}
                  {sub.next_renewal_at && (
                    <>
                      {exp && <span className="mx-2">·</span>}
                      <span data-testid="payment-method-next-renewal">
                        Next renewal {fmtDate(sub.next_renewal_at)}
                      </span>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="font-display font-bold text-lg" data-testid="payment-method-none">
                  No card on file
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
                  <Info size={12} /> Purchase a plan to save a card, or add one below.
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col md:items-end gap-2">
          <button
            onClick={startUpdate}
            disabled={busy}
            data-testid="payment-method-update-btn"
            className="inline-flex items-center gap-2 border border-zinc-950 text-xs font-mono tracking-[0.15em] uppercase px-4 py-3 hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-50"
          >
            <ArrowsCounterClockwise size={13} weight="bold" />
            {busy ? "REDIRECTING…" : hasCard ? "UPDATE CARD" : "ADD CARD"}
          </button>
          <button
            onClick={runProbe}
            disabled={probing}
            data-testid="save-card-verify-btn"
            className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.12em] uppercase text-zinc-600 hover:text-zinc-950 disabled:opacity-50"
          >
            {probing ? <CircleNotch size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
            {probing ? "PROBING TAP…" : "VERIFY WITH TAP"}
          </button>
        </div>
      </div>

      {/* Currency picker + credit */}
      <div className="border-t border-zinc-200 px-6 py-4 bg-zinc-50 flex flex-wrap items-center justify-between gap-3" data-testid="tenant-currency-row">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Billing currency</span>
            <select
              value={currency}
              onChange={(e) => saveCurrency(e.target.value)}
              disabled={savingCurrency}
              data-testid="tenant-currency-select"
              className="border border-zinc-300 bg-white px-2 py-1 text-xs font-mono focus:border-zinc-950 outline-none"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {Number(sub.renewal_credit) > 0 && (
            <div className="flex items-center gap-1.5 text-[11px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid="renewal-credit-pill">
              <span className="tracking-[0.12em] uppercase">Credit banked</span>
              <span className="font-bold tnum">{formatCurrency(sub.renewal_credit, tenant?.preferred_currency)}</span>
              <span className="text-emerald-600">· applied at next renewal</span>
            </div>
          )}
        </div>
        <div className="text-[11px] font-mono text-zinc-500 flex items-center gap-1.5">
          <Info size={11} />
          We charge $1 (auto-voided) to verify a card. No money moves.
        </div>
      </div>
    </section>
  );
}
